import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft, Mail, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { localBusinessSchema } from "@/lib/localBusiness";
import { siteImages, socialLinks } from "@/lib/siteData";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type GalleryRow = Database["public"]["Tables"]["gallery_images"]["Row"];
type TeamRow = Database["public"]["Tables"]["team_members"]["Row"];
type TestimonialRow = Database["public"]["Tables"]["testimonials"]["Row"];
type BlogPostRow = Database["public"]["Tables"]["blog_posts"]["Row"];

/* ─── Hero Slider ─── */
const heroSlides = [
  {
    image: siteImages.heroSlider1,
    subtitle: "VISUAL PRODUCTION STUDIO IN",
    title: "MYSTUDIO COLUMBUS, GA.",
    tagline: "STUDIO RENTAL — PHOTOGRAPHY — VIDEO SERVICES",
    body: "Since being founded in 2017, Our goal has always been the same. That has been to Provide a quality visual production space for us and other content creators, photographers, and videographers to create strong visual content!",
    cta: { label: "LET'S CREATE", to: "/book" },
    secondary: { label: "VIEW OUR SERVICES", to: "/services" },
  },
  {
    image: siteImages.heroSlider2,
    subtitle: "A PLACE FOR CREATIVES TO",
    title: "CREATE.",
    tagline: "",
    body: "MYSTUDIO IS YOUR STUDIO! We pride ourselves on providing creatives a space to create content and do it somewhere it feels like their own personal studio.",
    cta: { label: "ABOUT OUR STUDIO", to: "/about" },
    secondary: { label: "VIEW OUR WORKS", to: "/gallery" },
  },
];

const Hero = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setCurrent((c) => (c + 1) % heroSlides.length), 7000);
    return () => clearInterval(interval);
  }, []);

  const slide = heroSlides[current];

  return (
    <section className="relative h-screen flex items-end overflow-hidden">
      {heroSlides.map((s, i) => (
        <img
          key={i}
          src={s.image}
          alt={i === 0 ? `MyStudio Columbus, GA — ${s.title}, ${s.subtitle}` : ""}
          aria-hidden={i === 0 ? undefined : true}
          width={1920}
          height={1080}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === current ? "opacity-100" : "opacity-0"}`}
          fetchPriority={i === 0 ? "high" : undefined}
          loading={i === 0 ? "eager" : "lazy"}
          decoding={i === 0 ? "sync" : "async"}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/60 to-transparent z-10" />
      <div className="relative z-20 container mx-auto px-4 lg:px-8 pb-24 max-w-3xl mr-auto">
        <p className="text-primary font-mono text-sm tracking-[0.3em] mb-2 uppercase">{slide.subtitle}</p>
        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display tracking-wider leading-none mb-2">
          {slide.title}
        </h1>
        <div className="w-full h-1 bg-primary mb-6" />
        {slide.tagline && (
          <p className="text-foreground/80 font-mono text-xs tracking-[0.2em] uppercase mb-6">{slide.tagline}</p>
        )}
        <p className="text-foreground/70 font-body text-sm md:text-base leading-relaxed mb-8 uppercase max-w-xl">
          {slide.body}
        </p>
        <Link to={slide.cta.to} className="inline-flex items-center gap-2 text-primary font-display tracking-[0.2em] text-sm hover:gap-4 transition-all group">
          {slide.cta.label} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      {/* Yellow bottom-right CTA block */}
      <Link to={slide.secondary.to} className="absolute bottom-0 right-0 z-20 bg-primary text-primary-foreground px-8 py-6 font-display tracking-[0.15em] text-sm flex items-center gap-3 hover:bg-primary/90 transition-colors">
        {slide.secondary.label} <ArrowRight className="w-4 h-4" />
      </Link>
      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`w-3 h-3 border-2 transition-colors ${i === current ? "bg-primary border-primary" : "border-foreground/50 bg-transparent"}`}
          />
        ))}
      </div>
    </section>
  );
};

/* ─── About / Intro Block ─── */
const IntroBlock = () => (
  <section className="py-20 lg:py-28">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <div>
          <h2 className="text-base md:text-lg font-body leading-relaxed text-foreground/80 mb-6">
            MyStudio Founded in 2017 with the sole intent to assist creatives with a place where they could create strong studio content. Whether it's a Photo or Video Shoot, we have space to accommodate your studio needs.
          </h2>
          <div className="flex items-center gap-3 mt-4">
            {[
              { href: `mailto:bookmystudiospace@gmail.com`, icon: "✉", label: "Email MyStudio" },
              { href: socialLinks.facebook, icon: "f", label: "Follow MyStudio on Facebook" },
              { href: socialLinks.linkedin, icon: "in", label: "Connect with MyStudio on LinkedIn" },
              { href: socialLinks.pinterest, icon: "P", label: "Follow MyStudio on Pinterest" },
              { href: socialLinks.twitter, icon: "X", label: "Follow MyStudio on X (Twitter)" },
              { href: socialLinks.youtube, icon: "▶", label: "Subscribe to MyStudio on YouTube" },
              { href: socialLinks.instagram, icon: "📷", label: "Follow MyStudio on Instagram" },
            ].map((s, i) => (
              <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-8 h-8 bg-primary/10 flex items-center justify-center text-primary text-xs font-mono hover:bg-primary hover:text-primary-foreground transition-colors">
                <span aria-hidden="true">{s.icon}</span>
              </a>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm text-muted-foreground font-body leading-relaxed mb-6">
            If interested in using the space hit us up! Studio A is more of a general photography room and currently books at $45/hour. Studio B is a hybrid room used for video and photography, includes a cyc wall, and the current rate is $70 (or green screen setup). All in-house production services start $150.
          </p>
          <Link to="/book">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-[0.15em] px-8">
              BOOK NOW
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Services Section — Alternating full-bleed panels ─── */
const servicesData = [
  {
    title: "PHOTO SHOOT",
    subtitle: "Photo Services",
    tagline: "Commercial — Portrait — Fashion — Brand — Bridal & More!",
    body: "Our Expertise extends beyond one genre of Photography and that's exactly why we are considered full photography service providers in our area. Whether your needs are small or large scale, our network of skilled photographers are prepared to assist you with any project you need assistance with.",
    icon: siteImages.serviceIcon1,
    bgColor: "bg-primary",
    textColor: "text-primary-foreground",
    image: "/__l5e/assets-v1/a7df859d-ccc7-47f2-afb5-30501207a2db/Portfolio-BW-5-600x800.jpg",
    ctaLabel: "BLOG",
    ctaTo: "/blog",
    layout: "image-right",
  },
  {
    title: "PRODUCTION",
    subtitle: "Video Production Services",
    tagline: "Film & Cinematography.",
    body: "Let us help you tell your story with motion. We have a network of Filmmakers and Cinematographers that will help bring your video projects to life! Rest assured that we're not referring to the watered down same ole style of filmmaking. Let's work today!",
    icon: siteImages.serviceIcon2,
    bgColor: "bg-background",
    textColor: "text-foreground",
    image: siteImages.portfolioBW1,
    ctaLabel: "LET'S WORK",
    ctaTo: "/contact",
    layout: "image-left",
  },
  {
    title: "POST PRODUCTION SERVICES",
    subtitle: "",
    tagline: "TOP TIER PHOTO & VIDEO EDITING SERVICES.",
    body: "Ever needed help with enhancing your photo or film projects? Let us link you with the right editor to aid with giving your visuals new life! We have a network of professionals with over 10 years of experience in the realm of post production services.",
    icon: siteImages.editingIcon,
    bgColor: "bg-primary",
    textColor: "text-primary-foreground",
    image: "",
    ctaLabel: "EXPLORE POST-PRODUCTION SERVICES",
    ctaTo: "/services",
    layout: "wide",
  },
  {
    title: "PHOTO RESTORATION SERVICES",
    subtitle: "",
    tagline: "We Can Restore Your Old Photos.",
    body: "We understand how important those old damaged photos can be to a person. We all have them. Those images that mean so much but could not withstand the test of time. Our team uses advanced software practices to do our best to bring life back to those special images!",
    icon: "",
    bgColor: "bg-primary",
    textColor: "text-primary-foreground",
    image: "",
    ctaLabel: "LET'S WORK",
    ctaTo: "/contact",
    layout: "wide",
  },
];

const ServicesSection = () => (
  <section>
    {/* Photo Services - Yellow BG with image right */}
    <div className="bg-primary">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="p-8 lg:p-16 flex flex-col justify-center text-primary-foreground">
          <h2 className="text-5xl md:text-7xl font-display tracking-wider mb-2">PHOTO SHOOT</h2>
          <h3 className="text-xl font-display tracking-wider mb-1">Photo Services</h3>
          <p className="text-sm font-mono tracking-wider mb-6 opacity-80">Commercial — Portrait — Fashion — Brand — Bridal & More!</p>
          <div className="flex items-start gap-4 mb-6">
            <img src={siteImages.serviceIcon1} alt="" width={40} height={40} className="w-10 h-10 mt-1 object-contain" />
            <p className="text-sm leading-relaxed opacity-90 font-body">
              Our Expertise extends beyond one genre of Photography and that's exactly why we are considered full photography service providers in our area. Whether your needs are small or large scale, our network of skilled photographers are prepared to assist you with any project you need assistance with.
            </p>
          </div>
          <Link to="/blog" className="inline-flex items-center gap-2 text-primary-foreground font-display tracking-[0.2em] text-sm border-b-2 border-primary-foreground/50 pb-1 w-fit hover:border-primary-foreground transition-colors">
            BLOG <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="aspect-square lg:aspect-auto overflow-hidden">
          <img src="/__l5e/assets-v1/a7df859d-ccc7-47f2-afb5-30501207a2db/Portfolio-BW-5-600x800.jpg" alt="Black-and-white portrait captured at MyStudio Columbus, GA photo studio" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>

    {/* Video Production - Dark BG with image left */}
    <div className="bg-background">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="aspect-square lg:aspect-auto overflow-hidden relative">
          <div className="absolute inset-0 flex items-end p-8 z-10">
            <h2 className="text-5xl md:text-7xl font-display tracking-wider text-foreground/20">PRODUCTION</h2>
          </div>
          <img src={siteImages.portfolioBW1} alt="Cinematographer filming a video production at MyStudio Columbus, GA" className="w-full h-full object-cover" />
        </div>
        <div className="p-8 lg:p-16 flex flex-col justify-center">
          <h3 className="text-xl font-display tracking-wider mb-1">Video Production Services</h3>
          <p className="text-sm font-mono tracking-wider mb-6 text-muted-foreground">Film & Cinematography.</p>
          <div className="flex items-start gap-4 mb-6">
            <img src={siteImages.serviceIcon2} alt="" width={40} height={40} className="w-10 h-10 mt-1 object-contain" />
            <p className="text-sm text-foreground/80 leading-relaxed font-body">
              Let us help you tell your story with motion. We have a network of Filmmakers and Cinematographers that will help bring your video projects to life! Rest assured that we're not referring to the watered down same ole style of filmmaking. Let's work today!
            </p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 text-primary font-display tracking-[0.2em] text-sm border-b-2 border-primary/50 pb-1 w-fit hover:border-primary transition-colors">
            LET'S WORK <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>

    {/* Post Production - Yellow BG wide */}
    <div className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <h2 className="text-3xl md:text-5xl font-display tracking-wider mb-2">POST PRODUCTION SERVICES</h2>
        <p className="text-sm font-mono tracking-wider mb-8 opacity-80">TOP TIER PHOTO & VIDEO EDITING SERVICES.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="flex items-start gap-4">
            <img src={siteImages.editingIcon} alt="" width={64} height={64} className="w-16 h-16 object-contain" />
            <p className="text-sm leading-relaxed opacity-90 font-body">
              Ever needed help with enhancing your photo or film projects? Let us link you with the right editor to aid with giving your visuals new life! We have a network of professionals with over 10 years of experience in the realm of post production services.
            </p>
          </div>
          <div className="flex justify-end">
            <Link to="/services" className="inline-flex items-center gap-2 text-primary-foreground font-display tracking-[0.2em] text-sm border-b-2 border-primary-foreground/50 pb-1 hover:border-primary-foreground transition-colors">
              EXPLORE POST-PRODUCTION SERVICES <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>

    {/* Photo Restoration - Yellow BG with background image */}
    <div className="relative bg-primary text-primary-foreground overflow-hidden" style={{ minHeight: 300 }}>
      <div className="absolute inset-0 opacity-30">
        <img src={siteImages.portfolioBW6} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="relative z-10 container mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <h2 className="text-3xl md:text-5xl font-display tracking-wider mb-2">PHOTO RESTORATION SERVICES</h2>
        <p className="text-sm font-mono tracking-wider mb-6 opacity-80">We Can Restore Your Old Photos.</p>
        <p className="text-sm leading-relaxed opacity-90 font-body max-w-2xl mb-8">
          We understand how important those old damaged photos can be to a person. We all have them. Those images that mean so much but could not withstand the test of time. Our team uses advanced software practices to do our best to bring life back to those special images!
        </p>
        <Link to="/contact" className="inline-flex items-center gap-2 text-primary-foreground font-display tracking-[0.2em] text-sm border-b-2 border-primary-foreground/50 pb-1 w-fit hover:border-primary-foreground transition-colors">
          LET'S WORK <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </section>
);

/* ─── Studio Shots Gallery (live data; hides if empty) ─── */
const GallerySection = () => {
  const [images, setImages] = useState<GalleryRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    supabase.from("gallery_images").select("*").order("display_order").limit(10)
      .then(({ data }) => { setImages(data ?? []); setLoaded(true); });
  }, []);
  if (loaded && images.length === 0) return null;
  return (
  <section className="py-20 lg:py-28">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl md:text-4xl font-display tracking-wider">Studio Shots</h2>
          <p className="text-sm text-muted-foreground font-body mt-2">Over the years we've captured quite a few shots of users and clients here at the space!</p>
        </div>
        <Link to="/gallery" className="text-primary font-display tracking-[0.15em] text-sm hidden md:flex items-center gap-2 hover:gap-3 transition-all">
          VIEW THE SHOTS <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1">
        {images.slice(0, 5).map((item) => (
          <Link to="/gallery" key={item.id} className="group relative aspect-[3/4] overflow-hidden bg-muted">
            <img src={item.public_url} alt={item.alt_text || item.title || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            {item.title && (
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-start justify-end p-4">
                <h4 className="font-display tracking-wider text-sm">{item.title}</h4>
              </div>
            )}
          </Link>
        ))}
      </div>
      <Link to="/gallery" className="text-primary font-display tracking-[0.15em] text-sm flex items-center gap-2 mt-6 md:hidden">
        VIEW THE SHOTS <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
  );
};

/* ─── Team / Studio Regulars (live data; hides if empty) ─── */
const TeamSection = () => {
  const [team, setTeam] = useState<TeamRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    supabase.from("team_members").select("*").eq("active", true).order("display_order")
      .then(({ data }) => { setTeam(data ?? []); setLoaded(true); });
  }, []);
  if (loaded && team.length === 0) return null;
  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-display tracking-wider">Meet Some of the Studio Regulars</h2>
            <p className="text-sm text-muted-foreground font-body mt-1">We all shoot here in some capacity</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {team.map((m) => (
            <div key={m.id} className="group text-center">
              <div className="aspect-[3/4] overflow-hidden mb-3 bg-muted">
                {m.photo_url && <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />}
              </div>
              <h4 className="font-display tracking-wider text-sm">{m.name}</h4>
              {m.role && <p className="text-xs text-muted-foreground font-mono mt-1">{m.role}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Facilities ─── */
const FacilitiesSection = () => (
  <section className="py-20 lg:py-28 bg-surface">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-2xl md:text-3xl font-display tracking-wider mb-2">@Our Facilities</h2>
        <Link to="/about" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display tracking-[0.15em] text-xs px-6 py-2 hover:bg-primary/90 transition-colors">
          We have additional options available for you
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { img: siteImages.studioAid, title: "In-House Studio Professionals", desc: "Take advantage of our in-house professionals for your next project. Whether it's an editor, or shoot facilitator to help guide you through your next shoot, we got you covered!" },
          { img: siteImages.studioProp, title: "Additional Props Available", desc: "At MyStudio we realize that the difference between a good photo or video and a great photo or video could be a simple prop. We have some available in-house for use as well some large scale props that we rent out." },
          { img: siteImages.studioSet, title: "Set Builds & Designs", desc: "A good set adds new dimensions to your Visual Production and helps tell a story that may not be able to be told visually without it." },
        ].map((f) => (
          <div key={f.title} className="bg-panel border border-border overflow-hidden">
            <div className="aspect-video overflow-hidden">
              <img src={f.img} alt={f.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-6">
              <h3 className="font-display tracking-wider text-base mb-2">{f.title}</h3>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── CTA Banner with consultation image ─── */
const CTABanner = () => (
  <section className="relative overflow-hidden">
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
        <img src={siteImages.consultationImage} alt="MyStudio Columbus, GA studio consultation and rental space" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="bg-primary text-primary-foreground p-8 lg:p-16 flex flex-col justify-center">
        <p className="text-xs font-mono tracking-wider opacity-70 mb-2">Photo. Film. Studio Rental.</p>
        <h2 className="text-2xl md:text-3xl font-display tracking-wider mb-4">Our studio can assist with your next visual project</h2>
        <p className="text-sm font-body opacity-80 mb-6">Contact us today to see if we can assist in any way with your next photo or video project.</p>
        <Link to="/contact">
          <Button variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary font-display tracking-[0.15em] px-8">
            CONTACT US
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

/* ─── Equipment Brands ─── */
const EquipmentBrands = () => (
  <section className="py-16 bg-background">
    <div className="container mx-auto px-4">
      <div className="flex items-center justify-center gap-8 md:gap-16 flex-wrap">
        {(
          [
            [siteImages.sonyLogo, "Sony"],
            [siteImages.canonLogo, "Canon"],
            [siteImages.lumixLogo, "Lumix"],
            [siteImages.blackmagicLogo, "Blackmagic Design"],
            [siteImages.savageLogo, "Savage Universal"],
          ] as [string, string][]
        ).map(([logo, name]) => (
          <img key={name} src={logo} alt={`${name} — equipment brand supported at MyStudio`} width={120} height={40} className="h-6 md:h-10 w-auto object-contain invert" loading="lazy" />
        ))}
      </div>
    </div>
  </section>
);

/* ─── Testimonials (live; hides if empty) ─── */
const TestimonialsSection = () => {
  const [items, setItems] = useState<TestimonialRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    supabase.from("testimonials").select("*").order("display_order").limit(6)
      .then(({ data }) => { setItems(data ?? []); setLoaded(true); });
  }, []);
  if (loaded && items.length === 0) return null;
  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-display tracking-wider text-center mb-12">What Clients Say</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((t) => (
            <div key={t.id} className="bg-panel border border-border p-6">
              <p className="text-sm font-body italic mb-4">"{t.quote}"</p>
              <p className="font-display tracking-wider text-sm">{t.customer_name}</p>
              {t.project_type && <p className="text-xs text-muted-foreground font-mono mt-1">{t.project_type}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Blog Preview (live; hides if empty) ─── */
const BlogPreview = () => {
  const [posts, setPosts] = useState<BlogPostRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    supabase.from("blog_posts").select("*").eq("status", "published").order("publish_at", { ascending: false, nullsFirst: false }).limit(3)
      .then(({ data }) => { setPosts(data ?? []); setLoaded(true); });
  }, []);
  if (loaded && posts.length === 0) return null;
  return (
    <section className="py-20 lg:py-28 bg-surface">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-display tracking-wider mb-2">Blog</h2>
          <p className="text-sm text-muted-foreground font-body mb-4">Article, News, Tips</p>
          <Link to="/blog" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display tracking-[0.15em] text-xs px-6 py-2 hover:bg-primary/90 transition-colors">
            READ THE BLOG
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((p) => (
            <div key={p.id} className="bg-panel border border-border overflow-hidden group">
              {p.cover_image && (
                <div className="aspect-video overflow-hidden">
                  <img src={p.cover_image} alt={`Cover image for blog post: ${p.title}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
              )}
              <div className="p-5">
                <h3 className="font-display tracking-wider text-sm mb-2 group-hover:text-primary transition-colors">{p.title}</h3>
                {p.publish_at && <p className="text-xs text-muted-foreground font-mono mb-4">{new Date(p.publish_at).toLocaleDateString()}</p>}
                <Link to={`/blog/${p.slug}`} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-display tracking-[0.15em] text-xs px-4 py-2 hover:bg-primary/90 transition-colors">
                  Read the full blog post
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── HomePage ─── */
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "MyStudio",
  url: "https://bookmystudiospace.com",
  logo: "https://bookmystudiospace.com/favicon.svg",
  email: "info@bookmystudiospace.com",
  telephone: "+1-706-478-7640",
  sameAs: [
    "https://www.instagram.com/bookmystudiospace",
    "https://www.facebook.com/bookmystudiospace",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "2300 Whittlesey Rd Ste E",
    addressLocality: "Columbus",
    addressRegion: "GA",
    postalCode: "31909",
    addressCountry: "US",
  },
};

const Index = () => (
  <>
    <Helmet>
      <title>MyStudio Columbus GA | Photo & Video Studio Rental</title>
      <meta name="description" content="Premium photo & video studio rental in Columbus, GA. Book Studio A from $45/hr or Studio B from $70/hr. Your Studio. Your Vision." />
      <meta property="og:title" content="MyStudio Columbus GA | Photo & Video Studio Rental" />
      <meta property="og:description" content="Premium photo & video studio rental in Columbus, GA. Book Studio A from $45/hr or Studio B from $70/hr. Your Studio. Your Vision." />
      <meta property="og:url" content="https://bookmystudiospace.com" />
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema())}</script>
    </Helmet>
    <Hero />
    <IntroBlock />
    <ServicesSection />
    <GallerySection />
    <TeamSection />
    <TestimonialsSection />
    <FacilitiesSection />
    <CTABanner />
    <EquipmentBrands />
    <BlogPreview />
  </>
);

export default Index;
